import type {
  DeckIR,
  Fill,
  Hyperlink,
  ParseDiagnostic,
  Section,
  Shape,
  SlideIR,
  SlideLayout,
  SlideMaster,
  SlideTransition,
  TextStyleLevel,
  Theme,
  TransitionType,
} from '@slideforge/shared';
import { DEFAULT_COLOR_MAP, DiagnosticSink } from '@slideforge/shared';
import { OpcPackage, REL } from './opc.js';
import { buildMediaTable, type MediaTable } from './media.js';
import type { Element } from './xml.js';
import { parseFill, type FillContext } from './parse/dml.js';
import { parseShapeTree, type ShapeContext } from './parse/shapes.js';
import { parseTextBody } from './parse/text.js';
import { parseTheme } from './parse/theme.js';
import { parseMediaPlayback, parseTiming } from './parse/timing.js';
import { PropertyResolver, collectPlaceholders } from './property-resolver.js';

export interface ParseOptions {
  /** Called after each slide so a task pane can show slide 1 while 100 parses. */
  readonly onSlideParsed?: (index: number, total: number) => void;
}

export interface ParseResult {
  readonly deck: DeckIR;
  readonly media: MediaTable;
  readonly diagnostics: readonly ParseDiagnostic[];
  readonly elapsedMs: number;
}

/** Deck-level statistics, cheap enough to compute without a full parse. */
export interface DeckStats {
  readonly packageBytes: number;
  readonly partCount: number;
  readonly slideCount: number;
  readonly slidesWithTiming: number;
  readonly mediaPartCount: number;
}

const SECTION_EXT_URI = '{521415D9-36F7-43E2-AB2F-B90AF26B5E84}';

/**
 * Parses a .pptx into the Course IR.
 *
 * Never throws on malformed content: anything unrepresentable becomes a
 * `ParseDiagnostic` and is skipped, so one broken shape cannot cost the author
 * the other ninety-nine slides.
 */
export async function parseDeck(bytes: Uint8Array, options: ParseOptions = {}): Promise<ParseResult> {
  const started = now();
  const diagnostics = new DiagnosticSink();

  let pkg: OpcPackage;
  try {
    pkg = OpcPackage.open(bytes);
  } catch (error) {
    diagnostics.report(
      'blocking',
      'package.unreadable',
      `This file could not be opened as a PowerPoint package: ${error instanceof Error ? error.message : String(error)}`,
    );
    return { deck: emptyDeck(), media: emptyMedia(), diagnostics: diagnostics.items, elapsedMs: now() - started };
  }

  const media = await buildMediaTable(pkg);
  const presentationPart = findPresentationPart(pkg);
  if (!presentationPart) {
    diagnostics.report(
      'blocking',
      'package.no-presentation',
      'The package contains no presentation part, so it is not a .pptx file.',
    );
    return { deck: emptyDeck(), media, diagnostics: diagnostics.items, elapsedMs: now() - started };
  }

  const parser = new DeckParser(pkg, media, diagnostics, presentationPart);
  const deck = parser.parseAll(options);
  return { deck, media, diagnostics: diagnostics.items, elapsedMs: now() - started };
}

/** Package-level statistics without building the IR. Used by the M1 shell. */
export async function readDeckStats(bytes: Uint8Array): Promise<DeckStats> {
  const pkg = OpcPackage.open(bytes);
  const presentationPart = findPresentationPart(pkg);
  const slideParts = presentationPart
    ? pkg.relationshipsOfType(presentationPart, REL.slide).map((r) => r.resolvedPart ?? '')
    : [];
  let withTiming = 0;
  for (const part of slideParts) {
    if (part && pkg.xml(part).find((e) => e.name === 'p:timing')) withTiming += 1;
  }
  return {
    packageBytes: bytes.byteLength,
    partCount: pkg.partCount,
    slideCount: slideParts.length,
    slidesWithTiming: withTiming,
    mediaPartCount: pkg.partNames.filter((p) => /^\/ppt\/media\//i.test(p)).length,
  };
}

export class DeckParser {
  readonly #themes: Theme[] = [];
  readonly #themeIndexByPart = new Map<string, number>();
  readonly #masters: SlideMaster[] = [];
  readonly #masterIndexByPart = new Map<string, number>();
  readonly #layouts: SlideLayout[] = [];
  readonly #layoutIndexByPart = new Map<string, number>();
  readonly #slideIdByPart = new Map<string, string>();

  constructor(
    private readonly pkg: OpcPackage,
    private readonly media: MediaTable,
    private readonly diagnostics: DiagnosticSink,
    private readonly presentationPart: string,
  ) {}

  parseAll(options: ParseOptions = {}): DeckIR {
    const presentation = this.pkg.xml(this.presentationPart);
    const sldSz = presentation.child('p:sldSz');
    const notesSz = presentation.child('p:notesSz');

    const slideRefs = this.#slideReferences(presentation);
    for (const ref of slideRefs) this.#slideIdByPart.set(ref.partName, ref.id);

    // Masters and layouts first: a slide cannot resolve a placeholder without them.
    for (const rel of this.pkg.relationshipsOfType(this.presentationPart, REL.slideMaster)) {
      if (rel.resolvedPart) this.#ensureMaster(rel.resolvedPart);
    }

    const slides: SlideIR[] = [];
    slideRefs.forEach((ref, index) => {
      const slide = this.parseSlide(index, ref.partName, ref.id);
      if (slide) slides.push(slide);
      options.onSlideParsed?.(index, slideRefs.length);
    });

    return {
      title: this.#titleOf(),
      slideWidth: sldSz?.int('cx', 12192000) ?? 12192000,
      slideHeight: sldSz?.int('cy', 6858000) ?? 6858000,
      notesWidth: notesSz?.int('cx', 6858000) ?? 6858000,
      notesHeight: notesSz?.int('cy', 9144000) ?? 9144000,
      themes: this.#themes,
      masters: this.#masters,
      layouts: this.#layouts,
      slides,
      sections: this.#parseSections(presentation, slideRefs),
      media: [...this.media.assets.values()],
    };
  }

  parseSlide(index: number, partName: string, slideId: string): SlideIR | undefined {
    const root = this.pkg.xml(partName);
    if (root.name === '#missing' || root.name === '#invalid') {
      this.diagnostics.report('error', 'slide.unreadable', 'The slide part could not be read.', {
        slideIndex: index,
        partName,
      });
      return undefined;
    }

    const layoutRel = this.pkg.relationshipsOfType(partName, REL.slideLayout)[0];
    const layoutIndex = layoutRel?.resolvedPart ? this.#ensureLayout(layoutRel.resolvedPart) : -1;
    const layout = this.#layouts[layoutIndex];
    const master = layout ? this.#masters[layout.masterIndex] : undefined;

    const resolver = new PropertyResolver(
      layout ? collectPlaceholders(this.pkg.xml(layout.partName).path('p:cSld', 'p:spTree')) : [],
      master ? collectPlaceholders(this.pkg.xml(master.partName).path('p:cSld', 'p:spTree')) : [],
      master ? this.#textStylesOf(master.partName) : { title: undefined, body: undefined, other: undefined },
    );

    const cSld = root.child('p:cSld');
    const timing = parseTiming(root);
    // Media placement lives in the shape tree but playback lives in `p:timing`,
    // so the two are read separately and joined by shape id.
    const playback = parseMediaPlayback(root);
    const ctx: ShapeContext = {
      ...this.#shapeContext(partName, index, resolver),
      mediaPlaybackFor: (shapeId) => playback.get(shapeId),
    };
    const notesPart = this.pkg.relationshipsOfType(partName, REL.notesSlide)[0]?.resolvedPart;

    return {
      id: slideId,
      index,
      partName,
      layoutIndex,
      name: cSld?.attr('name') ?? `Slide ${index + 1}`,
      hidden: root.attr('show') === '0',
      showMasterShapes: root.bool('showMasterSp', true),
      ...(this.#background(cSld, ctx) ? { background: this.#background(cSld, ctx) as Fill } : {}),
      shapes: parseShapeTree(cSld?.child('p:spTree'), ctx),
      ...(notesPart ? { notes: this.#parseNotes(notesPart, index) } : {}),
      ...(this.#transition(root) ? { transition: this.#transition(root) as SlideTransition } : {}),
      ...(timing ? { timing } : {}),
    };
  }

  #slideReferences(presentation: Element): { id: string; partName: string }[] {
    const out: { id: string; partName: string }[] = [];
    for (const sldId of presentation.path('p:sldIdLst')?.all('p:sldId') ?? []) {
      const relId = sldId.attr('r:id');
      const part = this.pkg.resolve(this.presentationPart, relId);
      if (!part) continue;
      out.push({ id: `slide-${sldId.attr('id') ?? out.length + 1}`, partName: part });
    }
    return out;
  }

  #titleOf(): string {
    const core = this.pkg.xml('/docProps/core.xml');
    const title = core.child('dc:title')?.innerText().trim();
    if (title) return title;
    const app = this.pkg.xml('/docProps/app.xml');
    return app.child('TitlesOfParts')?.find((e) => e.name === 'vt:lpstr')?.innerText().trim() ?? 'Untitled';
  }

  #ensureMaster(partName: string): number {
    const existing = this.#masterIndexByPart.get(partName);
    if (existing !== undefined) return existing;

    const index = this.#masters.length;
    this.#masterIndexByPart.set(partName, index);

    const root = this.pkg.xml(partName);
    const themeRel = this.pkg.relationshipsOfType(partName, REL.theme)[0];
    const themeIndex = themeRel?.resolvedPart ? this.#ensureTheme(themeRel.resolvedPart, partName) : 0;

    const ctx = this.#shapeContext(partName);
    const cSld = root.child('p:cSld');
    const clrMap = root.child('p:clrMap');

    this.#masters.push({
      id: partName,
      partName,
      themeIndex,
      colorMap: clrMap
        ? {
            bg1: (clrMap.attr('bg1') ?? 'lt1') as SlideMaster['colorMap']['bg1'],
            tx1: (clrMap.attr('tx1') ?? 'dk1') as SlideMaster['colorMap']['tx1'],
            bg2: (clrMap.attr('bg2') ?? 'lt2') as SlideMaster['colorMap']['bg2'],
            tx2: (clrMap.attr('tx2') ?? 'dk2') as SlideMaster['colorMap']['tx2'],
          }
        : DEFAULT_COLOR_MAP,
      ...(this.#background(cSld, ctx) ? { background: this.#background(cSld, ctx) as Fill } : {}),
      shapes: parseShapeTree(cSld?.child('p:spTree'), ctx),
      titleStyle: [],
      bodyStyle: [],
      otherStyle: [],
    });

    // Layouts referenced by this master are parsed eagerly: a slide will need one.
    for (const rel of this.pkg.relationshipsOfType(partName, REL.slideLayout)) {
      if (rel.resolvedPart) this.#ensureLayout(rel.resolvedPart);
    }
    return index;
  }

  #ensureLayout(partName: string): number {
    const existing = this.#layoutIndexByPart.get(partName);
    if (existing !== undefined) return existing;

    const index = this.#layouts.length;
    this.#layoutIndexByPart.set(partName, index);
    // Reserve the slot before recursing: a master parses its layouts, and a
    // layout points back at its master.
    this.#layouts.push(PLACEHOLDER_LAYOUT);

    const root = this.pkg.xml(partName);
    const masterRel = this.pkg.relationshipsOfType(partName, REL.slideMaster)[0];
    const masterIndex = masterRel?.resolvedPart ? this.#ensureMaster(masterRel.resolvedPart) : 0;

    const ctx = this.#shapeContext(partName);
    const cSld = root.child('p:cSld');
    const clrMapOvr = root.path('p:clrMapOvr', 'a:overrideClrMapping');

    this.#layouts[index] = {
      id: partName,
      partName,
      masterIndex,
      type: root.attr('type') ?? 'cust',
      name: cSld?.attr('name') ?? 'Layout',
      ...(clrMapOvr
        ? {
            colorMap: {
              bg1: (clrMapOvr.attr('bg1') ?? 'lt1') as SlideMaster['colorMap']['bg1'],
              tx1: (clrMapOvr.attr('tx1') ?? 'dk1') as SlideMaster['colorMap']['tx1'],
              bg2: (clrMapOvr.attr('bg2') ?? 'lt2') as SlideMaster['colorMap']['bg2'],
              tx2: (clrMapOvr.attr('tx2') ?? 'dk2') as SlideMaster['colorMap']['tx2'],
            },
          }
        : {}),
      ...(this.#background(cSld, ctx) ? { background: this.#background(cSld, ctx) as Fill } : {}),
      shapes: parseShapeTree(cSld?.child('p:spTree'), ctx),
      showMasterShapes: root.bool('showMasterSp', true),
    };
    return index;
  }

  #ensureTheme(partName: string, ownerPart: string): number {
    const existing = this.#themeIndexByPart.get(partName);
    if (existing !== undefined) return existing;
    const index = this.#themes.length;
    this.#themeIndexByPart.set(partName, index);
    this.#themes.push(parseTheme(this.pkg.xml(partName), this.#fillContext(ownerPart)));
    return index;
  }

  #textStylesOf(masterPart: string): {
    title: Element | undefined;
    body: Element | undefined;
    other: Element | undefined;
  } {
    const txStyles = this.pkg.xml(masterPart).child('p:txStyles');
    return {
      title: txStyles?.child('p:titleStyle'),
      body: txStyles?.child('p:bodyStyle'),
      other: txStyles?.child('p:otherStyle'),
    };
  }

  #background(cSld: Element | undefined, ctx: FillContext): Fill | undefined {
    const bg = cSld?.child('p:bg');
    if (!bg) return undefined;
    const bgPr = bg.child('p:bgPr');
    if (bgPr) return parseFill(bgPr, ctx);
    // `p:bgRef` points into the theme's background fill list; resolving it needs
    // the theme, so it is carried as an inherit and settled at compile time.
    return { kind: 'inherit' };
  }

  #parseNotes(notesPart: string, slideIndex: number) {
    const root = this.pkg.xml(notesPart);
    const ctx = this.#shapeContext(notesPart, slideIndex);
    const body = root
      .find((e) => e.name === 'p:sp' && phTypeOf(e) === 'body')
      ?.child('p:txBody');
    return parseTextBody(body, ctx);
  }

  #transition(root: Element): SlideTransition | undefined {
    const transition =
      root.child('p:transition') ??
      root.find((e) => e.name === 'p14:transition' || e.name === 'p:transition');
    if (!transition) return undefined;

    const kindEl = transition.children.find((c) => !c.name.endsWith('sndAc'));
    const rawName = kindEl?.name.replace(/^[a-z0-9]+:/i, '');
    const advanceAfter = transition.attr('advTm');

    return {
      type: transitionTypeOf(rawName),
      ...(rawName ? { rawName } : {}),
      ...(kindEl?.attr('dir') ? { direction: kindEl.attr('dir') } : {}),
      ...(transition.attr('dur') ? { durationMs: transition.int('dur', 0) } : {}),
      advanceOnClick: transition.bool('advClick', true),
      ...(advanceAfter !== undefined ? { advanceAfterMs: Number.parseInt(advanceAfter, 10) } : {}),
    };
  }

  #parseSections(presentation: Element, slideRefs: { id: string; partName: string }[]): Section[] {
    const ext = presentation
      .path('p:extLst')
      ?.all('p:ext')
      .find((e) => e.attr('uri') === SECTION_EXT_URI);
    const sectionLst = ext?.child('p14:sectionLst');
    if (!sectionLst) return [];

    const byNumericId = new Map(slideRefs.map((r) => [r.id.replace(/^slide-/, ''), r.id]));
    return sectionLst.all('p14:section').map((section, i) => ({
      id: section.attr('id') ?? `section-${i}`,
      name: section.attr('name') ?? `Section ${i + 1}`,
      slideIds: (section.path('p14:sldIdLst')?.all('p14:sldId') ?? [])
        .map((s) => byNumericId.get(s.attr('id') ?? ''))
        .filter((id): id is string => id !== undefined),
    }));
  }

  #fillContext(partName: string): FillContext {
    return {
      mediaIdFor: (relId) => {
        const part = this.pkg.resolve(partName, relId);
        return part ? this.media.byPart.get(part) : undefined;
      },
      onLinkedMedia: (relId) => {
        const rel = this.pkg.relationship(partName, relId);
        this.diagnostics.report(
          'blocking',
          'media.linked-not-embedded',
          `"${rel?.target ?? relId}" is linked rather than embedded, so it cannot be published. Re-insert it in PowerPoint with Insert rather than Link to File.`,
          { partName },
        );
      },
    };
  }

  #shapeContext(partName: string, slideIndex?: number, resolver?: PropertyResolver): ShapeContext {
    return {
      ...this.#fillContext(partName),
      diagnostics: this.diagnostics,
      partName,
      ...(slideIndex !== undefined ? { slideIndex } : {}),
      ...(resolver ? { resolver } : {}),
      hyperlinkFor: (relId): Hyperlink | undefined => {
        const rel = this.pkg.relationship(partName, relId);
        if (!rel) return undefined;
        if (rel.external) return { kind: 'external', url: rel.target };
        if (rel.resolvedPart) {
          const slideId = this.#slideIdByPart.get(rel.resolvedPart);
          if (slideId) return { kind: 'slide', slideId };
        }
        return undefined;
      },
    };
  }
}

function phTypeOf(sp: Element): string | undefined {
  return sp.path('p:nvSpPr', 'p:nvPr', 'p:ph')?.attr('type');
}

const TRANSITION_TYPES: Readonly<Record<string, TransitionType>> = {
  fade: 'fade',
  push: 'push',
  wipe: 'wipe',
  split: 'split',
  cut: 'cut',
  dissolve: 'dissolve',
  cover: 'cover',
  pull: 'pull',
  zoom: 'zoom',
  morph: 'morph',
};

function transitionTypeOf(raw: string | undefined): TransitionType {
  if (!raw) return 'none';
  return TRANSITION_TYPES[raw] ?? 'other';
}

function findPresentationPart(pkg: OpcPackage): string | undefined {
  const fromRels = pkg.relationshipsOfType('/', REL.officeDocument)[0]?.resolvedPart;
  if (fromRels && pkg.has(fromRels)) return fromRels;
  return pkg.has('/ppt/presentation.xml') ? '/ppt/presentation.xml' : undefined;
}

const PLACEHOLDER_LAYOUT: SlideLayout = {
  id: '',
  partName: '',
  masterIndex: 0,
  type: 'cust',
  name: '',
  shapes: [],
  showMasterShapes: true,
};

function emptyDeck(): DeckIR {
  return {
    title: 'Untitled',
    slideWidth: 12192000,
    slideHeight: 6858000,
    notesWidth: 6858000,
    notesHeight: 9144000,
    themes: [],
    masters: [],
    layouts: [],
    slides: [],
    sections: [],
    media: [],
  };
}

function emptyMedia(): MediaTable {
  return { assets: new Map(), bytes: new Map(), byPart: new Map() };
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

export type { Shape, TextStyleLevel };
